import { useEffect, useId, useRef, useState } from 'react'
import {
  MEETING_CONTEXT,
  type AnalyzeMeetingFilesContext,
  type MemberListItem,
  type ResolvedGlossaryTranslationTerm,
} from '@ai-meeting/shared'
import { LANGUAGES } from '@/constants/languages'
import {
  clampToMaxLength,
  MEETING_CONTEXT_TEXT_MAX_LENGTH,
  MEETING_TITLE_MAX_LENGTH,
} from '@/constants/meeting'
import { fmt } from '@/constants/ui-locale'
import { getLanguageDisplayName } from '@/utils/languageDisplayName'
import {
  BookIcon,
  ComputerDesktopIcon,
  ExternalLinkIcon,
  InfoIcon,
  MicIcon,
  TrashIcon,
} from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { assertMeetingTitleAvailable, MeetingTitleTakenError } from '@/hooks/useCreateMeetingFlow'
import { analyzeMeetingFiles } from '@/services/ai'
import { useStore, type MeetingContext, type MeetingMode } from '@/store/useStore'
import {
  DEFAULT_SESSION_KIND,
  SELECTABLE_SESSION_KINDS,
  isOfflineSessionKind,
  type SessionKind,
} from '@/constants/session-kind'
import type { ContextGeneralEntry, ContextTranslationTerm } from '@soniox/client'
import {
  CONTEXT_FILE_ACCEPT,
  getFileExtension,
  validateContextFile,
} from './context-file-validation'
import {
  fetchGoogleMeetConnection,
  getGoogleMeetConnectUrl,
  type GoogleMeetConnectionStatus,
} from '@/services/native-transcription'
import { resolveGlossaryDictionaries } from '@/services/glossary'
import GlossaryMultiSelect from './GlossaryMultiSelect'
import ShareWithMembersField from './ShareWithMembersField'
import FieldTooltip from './FieldTooltip'
import SelectField from '@/components/SelectField'
import CreateMeetingSection from './CreateMeetingSection'
import ProjectSelect, { OTHER_PROJECT_OPTION_VALUE } from '@/components/ProjectSelect'
import { SEND_SCREEN_FRAMES_ENABLED } from '@/constants/feature-flags'
import { logActivity } from '@/services/activity'

export type SttProvider = 'soniox' | 'azure' | 'google_live_translate' | 'gpt-realtime-whisper'

export interface CreateMeetingPayload {
  title: string
  sourceLang: string
  targetLang: string
  context: MeetingContext
  mode: MeetingMode
  sessionKind: SessionKind
  sttProvider: SttProvider
  microphoneDeviceId: string
  microphoneLabel: string
  shareTabAudioConfirmed: boolean
  sendScreenFrames: boolean
  /** False when no microphone hardware was detected — the meeting runs tab-audio-only. */
  hasMicrophone: boolean
  glossaryId: string | null
  /** Backlog project key, or `''` when the form left the picker alone. */
  backlogProjectKey: string
  transcriptionSource: 'browser' | 'teams-native' | 'google-meet-native'
  nativeMeetingReference: string
  /**
   * BrSE.ai user ids to share the meeting with the moment it exists (BRAI-533),
   * so they are invited into the room this meeting declares. Empty for a
   * meeting kept private, and always empty when joining somebody else's room.
   */
  shareWithUserIds: string[]
  /**
   * Room to declare to the AI proxy (BRAI-533), or null for a meeting that
   * shares nothing. Minted here when recipients are picked, and taken from the
   * invite when this form was opened by Join — either way the meeting created
   * belongs to whoever is filling in the form.
   */
  sharedRoomId: string | null
}

interface CreateMeetingModalProps {
  /** Pre-resolved, non-duplicate default name from the host (empty while loading). */
  defaultTitle: string
  /** True while the host is fetching the default name from the server. */
  isResolvingTitle: boolean
  /**
   * Backlog project detected for the meeting the reader is sitting in
   * (BRAI-745). Adopted when it arrives and never after the reader has
   * picked for themselves; empty means "no suggestion", not "clear it".
   */
  suggestedProjectKey?: string
  initialTranscriptionSource?: CreateMeetingPayload['transcriptionSource']
  initialNativeMeetingReference?: string
  /**
   * `'panel'` renders the narrow single-column form used inside the Teams
   * meeting side panel, which drops the fields that only apply to a
   * browser-captured meeting. Everything still submitted is unchanged.
   */
  variant?: 'default' | 'panel'
  onClose: () => void
  onStart: (payload: CreateMeetingPayload) => void | Promise<void>
}

const DEFAULT_TARGET_LANG = 'vi'
/**
 * The model every meeting runs on, and the only one the form still reports.
 *
 * It is the one model that survives every combination the form allows — Two-way
 * translation, and a bot-captured source — which is what made stating it
 * possible where a picker was not.
 */
const DEFAULT_STT_PROVIDER: SttProvider = 'soniox'
const EMPTY_SOURCE_LANG = ''

/**
 * Columns for the row under the meeting name, by how many fields are in it.
 *
 * It was a fixed three, and the Mode is not always one of them — a
 * bot-captured source pins it and takes the field away, which left Project
 * and Share with holding two thirds of a row with the last third empty.
 * Written out rather than interpolated: Tailwind reads these as text.
 */
const FILING_ROW_COLUMNS: Record<number, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
}

/**
 * The cards in step 2, in the design's order: the two ways a browser-captured
 * meeting can run, then the bots that join a call on their own.
 *
 * Google Meet has no card. It is still a source the form can be opened on —
 * the join URL and its connect button are below — but it is not offered as a
 * way to start one, so three cards fill the row instead of four leaving one
 * stranded on a second.
 */
const MEETING_TYPE_CARDS = [
  {
    value: 'online',
    icon: ComputerDesktopIcon,
    titleKey: 'meetingTypeOnline',
    descKey: 'meetingTypeOnlineDesc',
  },
  {
    value: 'offline',
    icon: MicIcon,
    titleKey: 'meetingTypeOffline',
    descKey: 'meetingTypeOfflineDesc',
  },
  {
    value: 'teams-native',
    icon: ExternalLinkIcon,
    titleKey: 'transcriptionSourceTeams',
    descKey: 'meetingTypeTeamsDesc',
  },
] as const

interface ContextFileItem {
  id: string
  file: File
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getContextFileExtensionLabel(filename: string): string {
  const extension = getFileExtension(filename)
  if (extension === 'htm') return 'HTML'
  return extension.toUpperCase() || 'FILE'
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === 'object' && !Array.isArray(item)
  )
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

function uniqueBySerializedValue<T>(items: T[]): T[] {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = typeof item === 'string' ? item : JSON.stringify(item)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function normalizeGeneralRecord(item: Record<string, unknown>): ContextGeneralEntry[] {
  if (typeof item.key === 'string' && typeof item.value === 'string') {
    return [{ key: item.key.trim(), value: item.value.trim() }].filter(
      (entry) => entry.key && entry.value
    )
  }

  return Object.entries(item)
    .filter(([, value]) => typeof value === 'string' || typeof value === 'number')
    .map(([key, value]) => ({ key, value: String(value).trim() }))
    .filter((entry) => entry.key && entry.value)
}

function uniqueGeneralByKey(items: Record<string, unknown>[]): ContextGeneralEntry[] {
  const keyed = new Set<string>()
  const entries = items.flatMap(normalizeGeneralRecord)

  return entries.filter((entry) => {
    if (keyed.has(entry.key)) return false
    keyed.add(entry.key)
    return true
  })
}

function normalizeTranslationTermRecord(
  item: Record<string, unknown>
): ContextTranslationTerm | null {
  if (typeof item.source === 'string' && typeof item.target === 'string') {
    const source = item.source.trim()
    const target = item.target.trim()
    return source && target ? { source, target } : null
  }

  const stringValues = Object.values(item).filter(
    (value): value is string => typeof value === 'string' && value.trim().length > 0
  )
  if (stringValues.length < 2) return null

  return {
    source: stringValues[0].trim(),
    target: stringValues[1].trim(),
  }
}

function mergeTranslationTermsByPriority(
  ...groups: Record<string, unknown>[][]
): ContextTranslationTerm[] {
  const seen = new Set<string>()

  return groups
    .flat()
    .map(normalizeTranslationTermRecord)
    .filter((item): item is ContextTranslationTerm => item !== null)
    .filter((item) => {
      const key = item.source.toLocaleLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

function readDefaultContext(): AnalyzeMeetingFilesContext {
  return {
    general: asRecordArray(MEETING_CONTEXT.general),
    terms: asStringArray(MEETING_CONTEXT.terms),
    translationTerms: asRecordArray(MEETING_CONTEXT.translationTerms),
  }
}

function normalizeAnalyzeContext(value: unknown): AnalyzeMeetingFilesContext {
  if (typeof value === 'string') {
    return { text: value }
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }

  const source = value as Record<string, unknown>
  return {
    general: asRecordArray(source.general),
    text: typeof source.text === 'string' ? source.text : '',
    terms: asStringArray(source.terms),
    translationTerms: asRecordArray(source.translationTerms),
  }
}

function buildMeetingContext(
  analyzedContext?: unknown,
  userGlossaryTerms: ResolvedGlossaryTranslationTerm[] = []
): MeetingContext {
  const fallback = readDefaultContext()
  const analyzed = normalizeAnalyzeContext(analyzedContext)
  const general = uniqueGeneralByKey([...(analyzed.general ?? []), ...(fallback.general ?? [])])
  const terms = uniqueBySerializedValue([...(analyzed.terms ?? []), ...(fallback.terms ?? [])])
  const translationTerms = mergeTranslationTermsByPriority(
    userGlossaryTerms.map(({ source, target }) => ({ source, target })),
    analyzed.translationTerms ?? [],
    fallback.translationTerms ?? []
  )

  return {
    general,
    text: analyzed.text?.trim() || '',
    terms,
    translation_terms: translationTerms,
  }
}

interface MicrophoneDevice {
  deviceId: string
  label: string
}

async function getMicrophoneDevices(
  defaultMicLabel: string,
  micLabelTemplate: string
): Promise<MicrophoneDevice[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return []

  let devices = await navigator.mediaDevices.enumerateDevices()
  const hasAudioInputLabel = devices.some(
    (device) => device.kind === 'audioinput' && device.label.trim().length > 0
  )

  if (!hasAudioInputLabel && navigator.mediaDevices.getUserMedia) {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    stream.getTracks().forEach((track) => track.stop())
    devices = await navigator.mediaDevices.enumerateDevices()
  }

  return devices
    .filter((device) => device.kind === 'audioinput')
    .map((device, index) => ({
      deviceId: device.deviceId,
      label:
        device.label.trim() ||
        (index === 0 ? defaultMicLabel : fmt(micLabelTemplate, { n: index + 1 })),
    }))
}

export default function CreateMeetingModal({
  defaultTitle,
  isResolvingTitle,
  suggestedProjectKey = '',
  initialTranscriptionSource = 'browser',
  initialNativeMeetingReference = '',
  variant = 'default',
  onClose,
  onStart,
}: CreateMeetingModalProps) {
  const { t } = useUiStrings()
  const overlayRef = useRef<HTMLDivElement>(null)
  const modalBodyRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const titleErrorId = useId()
  const sourceLangId = useId()
  const targetLangId = useId()
  const sessionKindId = useId()
  const contextId = useId()
  const glossaryId = useId()
  const projectFieldId = useId()
  const nativeMeetingReferenceId = useId()
  const nativeMeetingReferenceErrorId = useId()
  const pendingMeetingInvite = useStore((state) => state.pendingMeetingInvite)
  const inviteRoomId = pendingMeetingInvite?.sharedRoomId ?? null
  const isJoiningSharedRoom = inviteRoomId !== null
  const [title, setTitle] = useState(defaultTitle)
  const [titleEdited, setTitleEdited] = useState(false)
  const [titleError, setTitleError] = useState<string | null>(null)
  const [contextTextError, setContextTextError] = useState<string | null>(null)
  const [targetLang, setTargetLang] = useState(
    pendingMeetingInvite?.targetLang ?? DEFAULT_TARGET_LANG
  )
  const [mode, setMode] = useState<MeetingMode>(
    pendingMeetingInvite?.sourceLang ? 'two_way' : 'one_way'
  )
  const [sessionKind, setSessionKind] = useState<SessionKind>(DEFAULT_SESSION_KIND)
  const [sourceLang, setSourceLang] = useState(
    pendingMeetingInvite?.sourceLang ?? EMPTY_SOURCE_LANG
  )
  const [contextText, setContextText] = useState('')
  const [contextFiles, setContextFiles] = useState<ContextFileItem[]>([])
  const [contextFileError, setContextFileError] = useState<string | null>(null)
  const [selectedGlossaryIds, setSelectedGlossaryIds] = useState<string[]>([])
  const [backlogProjectKey, setBacklogProjectKey] = useState('')
  const [projectEdited, setProjectEdited] = useState(false)
  // Kept as whole members, not ids: the summary renders names, and a failed
  // create must leave the picked list exactly as the user left it.
  const [shareWithMembers, setShareWithMembers] = useState<MemberListItem[]>([])
  /**
   * Set when Join on an invite opened this form. It makes the meeting about to
   * be created join an existing room rather than start one, which is also why
   * the recipient picker is hidden: they are joining, not inviting.
   */
  const [glossaryConflictCount, setGlossaryConflictCount] = useState(0)
  const [glossaryResolveError, setGlossaryResolveError] = useState(false)
  const [isResolvingGlossary, setIsResolvingGlossary] = useState(false)
  const [nativeMeetingReferenceError, setNativeMeetingReferenceError] = useState<string | null>(null)
  const [isAnalyzingContext, setIsAnalyzingContext] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [shareTabAudioConfirmed, setShareTabAudioConfirmed] = useState(true)
  /**
   * Never set any more: the checkbox that offered it went with the pair the
   * design replaced with a line of text. Kept rather than inlined as `false`
   * so the capability still has a name here — turning it back on now needs a
   * control as well as `SEND_SCREEN_FRAMES_ENABLED`.
   */
  const [sendScreenFrames] = useState(false)
  const [microphones, setMicrophones] = useState<MicrophoneDevice[]>([])
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState('')
  const [microphoneError, setMicrophoneError] = useState<string | null>(null)
  const [microphoneCheckComplete, setMicrophoneCheckComplete] = useState(false)
  const [transcriptionSource, setTranscriptionSource] = useState<
    CreateMeetingPayload['transcriptionSource']
  >(initialTranscriptionSource)
  const [nativeMeetingReference, setNativeMeetingReference] = useState(
    initialNativeMeetingReference
  )
  // The panel fixes the source to Teams and gets its audio from the gateway
  // bot, so the microphone, tab-audio and source pickers have nothing to act
  // on there. The meeting name is hidden too but still submitted — it is
  // seeded from the Teams calendar title (see `resolveTeamsMeetingName`).
  const isPanel = variant === 'panel'
  // A native source is captured by the gateway bot, not by this browser: the microphone and the
  // shared-tab audio never reach the transcript, and only Soniox is what those meetings actually
  // run on. Covers Google Meet as well as Teams — the bot is the audio source in both.
  const isNativeSource = transcriptionSource !== 'browser'
  // Teams withholds the join URL without the RSC grant; the panel says the
  // user can paste it here, so the field has to stay when nothing was seeded.
  const needsMeetingReferenceInput = !isPanel || !initialNativeMeetingReference.trim()
  const sessionKindLabels: Record<SessionKind, string> = {
    meeting: t.sessionKindMeeting,
    interview: t.sessionKindInterview,
    presentation: t.sessionKindPresentation,
    internal_meeting: t.sessionKindInternalMeeting,
  }
  const [googleConnection, setGoogleConnection] = useState<GoogleMeetConnectionStatus | null>(null)
  const googleMeetMediaEnabled = import.meta.env.VITE_GOOGLE_MEET_MEDIA_ENABLED === 'true'
  // Why a transcription source cannot be picked, keyed by source. A source with
  // an entry here is shown but not selectable, and the reason is what the button
  // says on hover — a greyed-out button with no explanation reads as a bug.
  //
  // Teams is not in here: it is pickable, and the join URL is pasted into the
  // field below. Only Google Meet is, and only while its preview flag is off.
  //
  // Neither entry is the real gate. The server keeps its own switch per
  // provider — `NATIVE_TEAMS_ENABLED` / `NATIVE_GOOGLE_MEET_ENABLED`, checked in
  // `assertProviderEnabled` — and a source turned off there fails at Start with
  // a 422 `PROVIDER_DISABLED`, whatever this map says. Nothing exposes those
  // flags to the browser, so this map cannot mirror them; it is only the
  // reasons this form already knows about.
  const sourceUnavailableReason: Partial<
    Record<CreateMeetingPayload['transcriptionSource'], string>
  > = {
    ...(googleMeetMediaEnabled
      ? {}
      : { 'google-meet-native': t.transcriptionSourceGoogleMeetPreviewRequired }),
  }
  // Only trust an empty device list once the enumerate/permission check has
  // actually finished — otherwise the select briefly flashes disabled while
  // still loading.
  const noMicrophoneDetected = microphoneCheckComplete && microphones.length === 0
  // Mic-only capture can't work without a microphone, so tab audio becomes
  // mandatory (and locked) once we know there is none.
  const effectiveShareTabAudioConfirmed = shareTabAudioConfirmed || noMicrophoneDetected
  // The frames come off the shared tab's display stream, so with no tab there is
  // nothing to send — the box reads unchecked there rather than promising something
  // that cannot happen.
  const effectiveSendScreenFrames =
    SEND_SCREEN_FRAMES_ENABLED && effectiveShareTabAudioConfirmed && sendScreenFrames
  /**
   * Whether the form asks for a Mode at all.
   *
   * It asks only where the answer can mean something. A meeting the Teams
   * gateway bot captures is a Normal Meeting (BRAI-586): the side panel runs
   * them that way, and picking Teams as the source here reaches the same bot,
   * so neither place puts the choice up.
   *
   * Scoped to Teams by name. Google Meet is the same bot and may well want the
   * same rule, but the ticket says Teams and a guess either way would be
   * invisible until someone hit it.
   */
  const sessionKindOffered = !isPanel && transcriptionSource !== 'teams-native'
  /**
   * The Mode the meeting actually runs as, which is not always the one in
   * state: Mode can be picked first and the source switched to Teams after,
   * and a Mode the user can no longer see must not be the one that ships.
   *
   * Derived rather than written back through `setSessionKind`, so switching
   * the source away from Teams hands back the Mode that was already picked
   * instead of silently resetting it.
   */
  const effectiveSessionKind = sessionKindOffered ? sessionKind : DEFAULT_SESSION_KIND
  // `effectiveSessionKind` already answers for the panel and for Teams — both
  // land on the default, which is not an offline kind.
  const isInternalSession = isOfflineSessionKind(effectiveSessionKind)
  /**
   * Whether the join URL is asked for. It used to sit under the source buttons
   * inside their own cell; it shares a cell with the Mode now, and this says
   * whether that cell has anything in it when the Mode does not.
   *
   * `!isInternalSession` was implicit while it lived in the source block,
   * which is hidden for an offline kind. It has to be said out loud here.
   */
  const meetingReferenceOffered =
    !isInternalSession && transcriptionSource !== 'browser' && needsMeetingReferenceInput
  /**
   * Interview is two people in a room, so there is no shared browser tab to
   * take audio or screenshots from and neither checkbox is offered (BRAI-586).
   *
   * Except on a machine with no microphone. Tab audio is the only input left
   * there, which is the whole reason `effectiveShareTabAudioConfirmed` forces
   * it on, and switching it off for the Mode would leave the meeting recording
   * silence. The pair stays visible in that one case, locked on, so the reason
   * is on screen rather than inferred from an empty transcript.
   */
  const tabCaptureUnavailable = effectiveSessionKind === 'interview' && !noMicrophoneDetected

  /**
   * Which of the meeting-type cards is on, derived rather than stored.
   *
   * Offline and Online are the same `transcriptionSource` — a browser-captured
   * meeting — and differ only in whether a tab is shared. That used to be a
   * checkbox under the microphone, which asked the question twice: once as
   * "where does the audio come from" and again as "will you share a tab".
   *
   * Read the same way the payload is, so what the card says is what gets sent:
   * Interview has no tab to capture and a machine with no microphone has
   * nothing but one, and both of those already decide the flag.
   */
  const meetingType =
    transcriptionSource !== 'browser'
      ? transcriptionSource
      : tabCaptureUnavailable || !effectiveShareTabAudioConfirmed
        ? 'offline'
        : 'online'

  /** The cards write both halves, because one of them is only implied by the card. */
  const chooseMeetingType = (choice: typeof meetingType) => {
    if (choice === 'offline' || choice === 'online') {
      setTranscriptionSource('browser')
      setShareTabAudioConfirmed(choice === 'online')
      return
    }
    setTranscriptionSource(choice)
  }

  useEffect(() => {
    if (!googleMeetMediaEnabled) return
    void fetchGoogleMeetConnection()
      .then(setGoogleConnection)
      .catch(() => setGoogleConnection(null))
  }, [googleMeetMediaEnabled])

  useEffect(() => {
    let active = true

    if (selectedGlossaryIds.length === 0) {
      setGlossaryConflictCount(0)
      setGlossaryResolveError(false)
      setIsResolvingGlossary(false)
      return () => {
        active = false
      }
    }

    setIsResolvingGlossary(true)
    setGlossaryResolveError(false)
    void resolveGlossaryDictionaries(selectedGlossaryIds)
      .then((response) => {
        if (!active) return
        setGlossaryConflictCount(response.conflictCount)
      })
      .catch(() => {
        if (!active) return
        setGlossaryConflictCount(0)
        setGlossaryResolveError(true)
      })
      .finally(() => {
        if (active) setIsResolvingGlossary(false)
      })

    return () => {
      active = false
    }
  }, [selectedGlossaryIds])

  // Adopt the server-resolved default name once it arrives, unless the user has
  // already typed their own title.
  useEffect(() => {
    if (!titleEdited) setTitle(defaultTitle)
  }, [defaultTitle, titleEdited])

  // Same bargain for the detected project: it arrives after the form does, so
  // it is adopted late — but a reader who has already chosen outranks it, and
  // an empty suggestion is not an instruction to clear what they chose.
  useEffect(() => {
    if (!projectEdited && suggestedProjectKey) setBacklogProjectKey(suggestedProjectKey)
  }, [suggestedProjectKey, projectEdited])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  useEffect(() => {
    let ignore = false

    async function loadMicrophones() {
      try {
        setMicrophoneError(null)
        const devices = await getMicrophoneDevices(t.defaultMicrophone, t.microphoneN)
        if (ignore) return

        setMicrophones(devices)
        setSelectedMicrophoneId((current) => current || devices[0]?.deviceId || '')
      } catch (error) {
        if (ignore) return
        setMicrophoneError(error instanceof Error ? error.message : t.microphoneLoadFailed)
      } finally {
        if (!ignore) setMicrophoneCheckComplete(true)
      }
    }

    void loadMicrophones()
    return () => {
      ignore = true
    }
  }, [t.defaultMicrophone, t.microphoneLoadFailed, t.microphoneN])

  const addContextFiles = (files: FileList | File[]) => {
    const incoming = Array.from(files)
    if (incoming.length === 0) return

    const validFiles: File[] = []
    let hasTypeError = false
    let hasSizeError = false

    for (const file of incoming) {
      const error = validateContextFile(file)
      if (error === 'type') hasTypeError = true
      if (error === 'size') hasSizeError = true
      if (!error) validFiles.push(file)
    }

    if (hasTypeError && hasSizeError) {
      setContextFileError(t.contextFileInvalidTypeAndSize)
    } else if (hasTypeError) {
      setContextFileError(t.contextFileInvalidType)
    } else if (hasSizeError) {
      setContextFileError(t.contextFileTooLarge)
    } else {
      setContextFileError(null)
    }

    if (validFiles.length === 0) return

    setContextFiles((current) => {
      const existingKeys = new Set(
        current.map((item) => `${item.file.name}-${item.file.size}-${item.file.lastModified}`)
      )
      const nextItems = validFiles
        .filter((file) => !existingKeys.has(`${file.name}-${file.size}-${file.lastModified}`))
        .map((file) => ({
          id: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
          file,
        }))
      return [...current, ...nextItems]
    })
  }

  const handleContextFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) addContextFiles(event.target.files)
    event.target.value = ''
  }

  const handleContextFileDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault()
    if (isStarting) return
    if (event.dataTransfer.files.length > 0) {
      addContextFiles(event.dataTransfer.files)
    }
  }

  const removeContextFile = (id: string) => {
    setContextFiles((current) => current.filter((item) => item.id !== id))
    setContextFileError(null)
  }

  const analyzeContextFiles = async (): Promise<unknown> => {
    if (contextFiles.length === 0) return undefined
    const response = await analyzeMeetingFiles(
      contextFiles.map((item) => item.file),
      targetLang
    )
    return response.context
  }

  const resolveContext = (
    analyzedContext: unknown,
    userGlossaryTerms: ResolvedGlossaryTranslationTerm[]
  ): MeetingContext => {
    const manualContext = contextText.trim()
    const analyzedMeetingContext = buildMeetingContext(analyzedContext, userGlossaryTerms)
    return manualContext
      ? { ...analyzedMeetingContext, text: manualContext }
      : analyzedMeetingContext
  }

  const scrollModalToTitleError = () => {
    if (overlayRef.current) {
      overlayRef.current.scrollTop = 0
    }
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0
    }
  }

  const handleStart = async () => {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      setTitleError(t.meetingNameRequired)
      scrollModalToTitleError()
      return
    }
    if (trimmedTitle.length > MEETING_TITLE_MAX_LENGTH) {
      setTitleError(t.meetingNameTooLong)
      scrollModalToTitleError()
      return
    }
    if (contextText.length > MEETING_CONTEXT_TEXT_MAX_LENGTH) {
      setContextTextError(fmt(t.fieldTooLong, { max: MEETING_CONTEXT_TEXT_MAX_LENGTH }))
      document.getElementById(contextId)?.focus()
      return
    }
    setTitleError(null)
    setContextTextError(null)
    setContextFileError(null)
    setGlossaryResolveError(false)
    setNativeMeetingReferenceError(null)
    if (transcriptionSource !== 'browser' && !nativeMeetingReference.trim()) {
      // Its own slot, not `contextFileError`: that one renders beside the glossary uploader at the
      // bottom of the form, so the message landed far from the empty field it was about.
      setNativeMeetingReferenceError(t.meetingJoinUrlRequired)
      document.getElementById(nativeMeetingReferenceId)?.focus()
      return
    }

    const selectedMicrophone = microphones.find(
      (microphone) => microphone.deviceId === selectedMicrophoneId
    )

    // Kept true for the whole flow below (title check, context analysis + onStart,
    // which triggers the browser's screen/tab-share picker) so a stray click on any
    // field can't land on the file input while the user is waiting on that
    // native prompt (BRAI-153).
    setIsStarting(true)
    try {
      try {
        await assertMeetingTitleAvailable(trimmedTitle)
      } catch (error) {
        if (error instanceof MeetingTitleTakenError) {
          setTitleError(error.inTrash ? t.meetingNameTakenInTrash : t.meetingNameTaken)
          scrollModalToTitleError()
          return
        }
        throw error
      }

      setIsAnalyzingContext(contextFiles.length > 0)
      setIsResolvingGlossary(selectedGlossaryIds.length > 0)
      const [glossaryResult, analyzedContextResult] = await Promise.allSettled([
        selectedGlossaryIds.length > 0
          ? resolveGlossaryDictionaries(selectedGlossaryIds)
          : Promise.resolve({
              translationTerms: [] as ResolvedGlossaryTranslationTerm[],
              conflictCount: 0,
            }),
        analyzeContextFiles(),
      ])
      setIsAnalyzingContext(false)
      setIsResolvingGlossary(false)

      if (glossaryResult.status === 'rejected') {
        setGlossaryResolveError(true)
      }
      if (analyzedContextResult.status === 'rejected') {
        const error = analyzedContextResult.reason
        setContextFileError(error instanceof Error ? error.message : t.contextAnalyzeFailed)
      }
      if (glossaryResult.status === 'rejected' || analyzedContextResult.status === 'rejected') {
        return
      }

      setGlossaryConflictCount(glossaryResult.value.conflictCount)
      const resolvedContext = resolveContext(
        analyzedContextResult.value,
        glossaryResult.value.translationTerms
      )

      try {
        await onStart({
          title: trimmedTitle,
          sourceLang: isInternalSession
            ? EMPTY_SOURCE_LANG
            : mode === 'two_way'
              ? sourceLang
              : EMPTY_SOURCE_LANG,
          targetLang,
          context: resolvedContext,
          mode: isInternalSession ? 'one_way' : mode,
          sessionKind: effectiveSessionKind,
          sttProvider: DEFAULT_STT_PROVIDER,
          microphoneDeviceId: selectedMicrophone?.deviceId ?? '',
          microphoneLabel: selectedMicrophone?.label ?? t.defaultMicrophone,
          shareTabAudioConfirmed:
            isInternalSession || tabCaptureUnavailable ? false : effectiveShareTabAudioConfirmed,
          sendScreenFrames:
            isInternalSession || tabCaptureUnavailable ? false : effectiveSendScreenFrames,
          hasMicrophone: !noMicrophoneDetected,
          glossaryId: null,
          // “Other” is a form choice, not a Backlog project key. Preserve it
          // in state so the closed picker can display the choice, then keep
          // the existing API contract by submitting no project.
          backlogProjectKey:
            backlogProjectKey === OTHER_PROJECT_OPTION_VALUE ? '' : backlogProjectKey,
          transcriptionSource: isInternalSession ? 'browser' : transcriptionSource,
          nativeMeetingReference: isInternalSession ? '' : nativeMeetingReference.trim(),
          shareWithUserIds: inviteRoomId
            ? []
            : shareWithMembers.map((member) => member.id),
          // A room is minted only when this form is the one starting it. Joining
          // reuses the invite's key, and a meeting with neither stays a room of
          // one by declaring nothing.
          sharedRoomId:
            inviteRoomId ??
            (shareWithMembers.length > 0 ? crypto.randomUUID() : null),
        })
      } catch (error) {
        if (error instanceof MeetingTitleTakenError) {
          setTitleError(error.inTrash ? t.meetingNameTakenInTrash : t.meetingNameTaken)
          scrollModalToTitleError()
          return
        }
      }
    } finally {
      setIsStarting(false)
    }
  }

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-3 backdrop-blur-sm sm:py-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-meeting-title"
    >
      <button
        type="button"
        aria-label={t.closeCreateMeetingModal}
        className="fixed inset-0 cursor-default"
        onClick={onClose}
      />

      <div
        className={[
          'animate-scale-in relative flex max-h-[calc(100dvh-1.5rem)] w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-2xl sm:max-h-[calc(100dvh-2rem)]',
          // Wide enough for two readable columns: at `max-w-2xl` they came
          // out ~320px each, which is narrower than the one-way/two-way
          // cards and the longer select labels want.
          isPanel ? 'max-w-none' : 'max-w-5xl',
        ].join(' ')}
        data-testid="create-meeting-modal"
      >
        <div
          className={[
            'shrink-0',
            isPanel ? 'px-3 pt-3 pb-2' : 'px-3 pt-3 pb-2 sm:px-6 sm:pt-6 sm:pb-3',
          ].join(' ')}
        >
          <h2
            id="create-meeting-title"
            className={[
              'font-bold text-[var(--text-primary)]',
              isPanel ? 'text-lg' : 'text-base sm:text-lg',
            ].join(' ')}
          >
            {t.createNewMeeting}
          </h2>
          <p
            className={[
              'mt-1.5 text-[var(--text-secondary)]',
              isPanel ? 'text-sm' : 'text-xs sm:text-sm',
            ].join(' ')}
          >
            {t.createMeetingSubtitle}
          </p>
        </div>

        <div
          ref={modalBodyRef}
          data-testid="create-meeting-modal-content"
          className={[
            'min-h-0 flex-1 overflow-y-auto overscroll-contain',
            isPanel ? 'px-3' : 'px-3 sm:px-6',
          ].join(' ')}
        >
        {/* Two columns in the main app, and they are now two declared
            columns rather than one grid everything flowed into. The flow
            version packed whatever came next into whatever cell was free, so
            what the meeting listens to could end up beside what it is told in
            advance — two unrelated questions sharing a row because a field
            above them happened to be hidden.
            The side panel stays one column: it is too narrow for two. */}
        <div
          className={
            isPanel
              ? 'space-y-3'
              : 'grid items-start gap-3 sm:grid-cols-[minmax(0,1.667fr)_minmax(0,1fr)] sm:gap-x-7'
          }
        >
          {/* The three numbered steps. */}
          <div className={isPanel ? 'space-y-3' : 'space-y-5'}>
          {/* The name input is hidden in the panel, so a duplicate-title 409
              would otherwise set `titleError` with nothing on screen to show
              it — and nothing the user could do about it. `scrollModalToTitleError`
              scrolls to the top, which is exactly where this sits. */}
          {isPanel && titleError && (
            <p
              className="rounded-xl border border-[var(--error)]/30 bg-[var(--error)]/10 px-3 py-2 text-xs text-[var(--error)]"
              role="alert"
            >
              {titleError}
            </p>
          )}

          {/* One row for the three things a meeting is filed under: its name,
              its project and who else gets it. They stack below `sm`, where
              three columns would leave each one too narrow to read what is in
              it. Two columns when there is nobody to invite — joining
              somebody else's room has no Share with. */}
          {!isPanel && (
            <CreateMeetingSection
              step={1}
              title={t.createMeetingStepDetails}
              testId="create-meeting-step-details"
            >
            <div
              data-testid="create-meeting-filing-row"
              className={[
                'grid items-start gap-4',
                // Project, plus Share with unless this is somebody else's
                // room, plus the Mode unless the source has pinned it.
                FILING_ROW_COLUMNS[1 + (inviteRoomId ? 0 : 1) + (sessionKindOffered ? 1 : 0)],
              ].join(' ')}
            >
              {/* The name takes the whole row, as the design has it: the
                  three fields under it say what the meeting is filed under,
                  and the name is not one of three — it is the thing being
                  filed. It also stops a long name from squeezing the pickers
                  beside it into a third of the row each. */}
              <div className="scroll-mt-6 sm:col-span-full">
                {/* The count rides the label's line. On its own line under
                    the input it made this cell the tallest of the three and
                    pushed the row open for nothing. */}
                <div className="mb-1 flex items-baseline justify-between gap-2 sm:mb-1.5">
                  <label
                    htmlFor={titleId}
                    className="text-xs font-semibold text-[var(--text-secondary)]"
                  >
                    {t.meetingName} <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[10px] font-semibold tabular-nums text-slate-600 dark:text-[var(--text-tertiary)]">
                    {(isResolvingTitle && !titleEdited ? '' : title).length} /{' '}
                    {MEETING_TITLE_MAX_LENGTH}
                  </span>
                </div>
                <input
                  id={titleId}
                  type="text"
                  value={isResolvingTitle && !titleEdited ? '' : title}
                  onChange={(event) => {
                    setTitleEdited(true)
                    const nextTitle = clampToMaxLength(event.target.value, MEETING_TITLE_MAX_LENGTH)
                    setTitle(nextTitle)
                    if (nextTitle.trim()) setTitleError(null)
                  }}
                  placeholder={isResolvingTitle ? t.meetingNameResolving : t.meetingNamePlaceholder}
                  required
                  maxLength={MEETING_TITLE_MAX_LENGTH}
                  disabled={isStarting}
                  aria-invalid={titleError ? 'true' : 'false'}
                  aria-describedby={titleError ? titleErrorId : undefined}
                  className={
                    titleError
                      ? 'input-field focus:ring-[var(--error)]/25 border-[var(--error)] focus:border-[var(--error)] disabled:cursor-not-allowed disabled:opacity-60'
                      : 'input-field disabled:cursor-not-allowed disabled:opacity-60'
                  }
                />
                {titleError && (
                  <p id={titleErrorId} className="mt-1.5 text-xs text-[var(--error)]" role="alert">
                    {titleError}
                  </p>
                )}
              </div>

            <ProjectSelect
              id={projectFieldId}
              value={backlogProjectKey}
              includeOther
              onChange={(projectKey) => {
                setProjectEdited(true)
                setBacklogProjectKey(projectKey)
              }}
              disabled={isStarting}
              // The settings page would otherwise render behind a modal
              // nobody dismissed, and a reader would be typing a key into
              // something they cannot see.
              onOpenSettings={onClose}
            />

            {/* Main app only: the Teams side panel is too narrow for a
                picker, and a meeting started from there already has its
                attendees. */}
            {!inviteRoomId && (
              <ShareWithMembersField
                selected={shareWithMembers}
                onChange={setShareWithMembers}
                disabled={isStarting}
              />
            )}
              {sessionKindOffered && (
                <div>
                  <SelectField
                    id={sessionKindId}
                    label={t.sessionKindLabel}
                    labelAccessory={
                      <FieldTooltip
                        accessibleLabel={t.sessionKindSelectionHint}
                        text={t.sessionKindSelectionHint}
                        testId="session-kind-hint"
                      />
                    }
                    value={sessionKind}
                    onChange={(value) => {
                      // The press, not the meeting: which kind a meeting ran as is
                      // on the row itself, and this answers the other question —
                      // whether people reach for this picker at all (BRAI-677).
                      logActivity('meeting.kind_selected')
                      setSessionKind(value as SessionKind)
                    }}
                    disabled={isStarting}
                  >
                    {SELECTABLE_SESSION_KINDS.map((option) => (
                      <option key={option} value={option}>
                        {sessionKindLabels[option]}
                      </option>
                    ))}
                  </SelectField>
                </div>
              )}
            </div>
            </CreateMeetingSection>
          )}

          {/* Joining somebody else's room: there is nobody to invite, and the
              one thing that matters is that this machine shares tab audio too. */}
          {inviteRoomId && (
            <div className="rounded-xl border border-[var(--accent)] bg-[var(--accent-light)] px-4 py-3 sm:col-span-2">
              <p className="text-sm font-semibold text-[var(--accent)]">
                {t.joiningSharedMeetingTitle}
              </p>
              <p className="mt-1 text-xs text-[var(--text-secondary)]">
                {t.joiningSharedMeetingHint}
              </p>
            </div>
          )}

          {!isInternalSession && (
          <CreateMeetingSection
            step={2}
            title={t.createMeetingStepType}
            testId="create-meeting-step-type"
          >
          <div>
            {!isPanel && (
              <>
                <label className="sr-only">{t.transcriptionSourceLabel}</label>
                <div
                  role="group"
                  aria-label={t.transcriptionSourceLabel}
                  data-testid="meeting-type-cards"
                  className="grid gap-3 sm:grid-cols-3"
                >
                  {MEETING_TYPE_CARDS.map(({ value, icon: Icon, titleKey, descKey }) => {
                    // Offline and Online are both `browser`, so an unavailable
                    // reason filed against the source applies to the pair.
                    const unavailable =
                      value === 'offline' || value === 'online'
                        ? sourceUnavailableReason.browser
                        : sourceUnavailableReason[value]
                    // Offline cannot be chosen where the microphone is the one
                    // input that is missing, and Online cannot where there is
                    // no tab to take — both already decide the payload, so the
                    // card says so rather than being overruled after the press.
                    const forced =
                      value === 'offline'
                        ? noMicrophoneDetected
                          ? t.noMicrophoneDetected
                          : undefined
                        : value === 'online' && tabCaptureUnavailable
                          ? t.sessionKindInterview
                          : undefined
                    const reason = unavailable ?? forced
                    const selected = meetingType === value
                    return (
                      <button
                        key={value}
                        type="button"
                        disabled={isStarting || reason !== undefined}
                        aria-pressed={selected}
                        title={reason}
                        data-testid={`meeting-type-${value}`}
                        onClick={() => chooseMeetingType(value)}
                        className={[
                          'rounded-xl border-2 px-4 py-3.5 text-left transition disabled:cursor-not-allowed disabled:opacity-50',
                          selected
                            ? 'border-[var(--accent)] bg-[var(--accent-light)]'
                            : 'border-[var(--border-color)] bg-[var(--bg-primary)]',
                        ].join(' ')}
                      >
                        <Icon
                          className={[
                            'h-4 w-4',
                            selected ? 'text-[var(--accent)]' : 'text-[var(--text-tertiary)]',
                          ].join(' ')}
                        />
                        <span className="mt-2 block text-sm font-semibold text-[var(--text-primary)]">
                          {t[titleKey]}
                        </span>
                        <span className="mt-0.5 block text-xs leading-5 text-[var(--text-secondary)]">
                          {t[descKey]}
                        </span>
                      </button>
                    )
                  })}
                </div>

                {/* Only under Online, which is the only card that asks the
                    reader to do something after pressing Start. It replaces
                    the checkbox that used to ask them to promise it first —
                    a promise the browser's own prompt collects anyway. */}
                {meetingType === 'online' && (
                  <p
                    data-testid="share-tab-audio-callout"
                    className="mt-3 flex items-start gap-2.5 rounded-[10px] border border-blue-200 bg-blue-50 px-3.5 py-3 text-xs leading-5 text-[var(--text-secondary)] dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)]"
                  >
                    <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
                    <span className="min-w-0 flex-1">{t.shareTabAudioCallout}</span>
                  </p>
                )}

                {/* `mt-4`, not nothing: the callout above it is a block of
                    prose with its own border, and the microphone's label
                    landed hard against its bottom edge — the two read as one
                    box with a stray label on it. */}
                {!isInternalSession && !isPanel && !isNativeSource && (
                  <div className="mt-4">
                    <SelectField
                      label={
                        <span className="flex items-center gap-1.5">
                          <MicIcon className="h-3.5 w-3.5" />
                          {t.microphone}
                        </span>
                      }
                      value={selectedMicrophoneId}
                      onChange={setSelectedMicrophoneId}
                      disabled={isStarting || noMicrophoneDetected}
                    >
                      {microphones.length > 0 ? (
                        microphones.map((microphone) => (
                          <option key={microphone.deviceId} value={microphone.deviceId}>
                            {microphone.label}
                          </option>
                        ))
                      ) : (
                        <option value="">
                          {noMicrophoneDetected ? t.noMicrophoneDetected : t.defaultMicrophone}
                        </option>
                      )}
                    </SelectField>
                    {microphoneError && (
                      <p className="mt-1.5 text-xs text-[var(--error)]">{microphoneError}</p>
                    )}
                    {noMicrophoneDetected && !microphoneError && (
                      <p className="mt-1.5 text-xs text-[var(--text-tertiary)]">{t.noMicrophoneDetected}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Inside step 2, under the card that asks for it. The Mode it used
              to share a cell with has gone up to step 1, where it belongs with
              the rest of what a meeting is filed under; what is left here is
              the one field a bot-captured source cannot start without. */}
          {meetingReferenceOffered && (
            <div className="space-y-3 sm:space-y-4">
            {meetingReferenceOffered ? (
              <div>
                  <label
                    htmlFor={nativeMeetingReferenceId}
                    className="mb-1 block text-xs font-semibold text-[var(--text-secondary)] sm:mb-1.5"
                  >
                    {t.meetingJoinUrlLabel} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={nativeMeetingReferenceId}
                    type="url"
                    value={nativeMeetingReference}
                    onChange={(event) => {
                      setNativeMeetingReference(event.target.value)
                      if (event.target.value.trim()) setNativeMeetingReferenceError(null)
                    }}
                    disabled={isStarting}
                    placeholder="https://teams.microsoft.com/l/meetup-join/..."
                    aria-invalid={nativeMeetingReferenceError ? 'true' : 'false'}
                    aria-describedby={
                      nativeMeetingReferenceError ? nativeMeetingReferenceErrorId : undefined
                    }
                    className={
                      nativeMeetingReferenceError
                        ? 'input-field focus:ring-[var(--error)]/25 border-[var(--error)] focus:border-[var(--error)]'
                        : 'input-field'
                    }
                  />
                  {nativeMeetingReferenceError && (
                    <p
                      id={nativeMeetingReferenceErrorId}
                      className="mt-1.5 text-xs text-[var(--error)]"
                      role="alert"
                    >
                      {nativeMeetingReferenceError}
                    </p>
                  )}
                  <p className="mt-1.5 text-xs text-[var(--text-tertiary)]">
                    {t.meetingJoinUrlHint}
                  </p>
                  {transcriptionSource === 'google-meet-native' &&
                  googleConnection?.status !== 'ACTIVE' ? (
                    <button
                      type="button"
                      className="mt-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white"
                      onClick={() => {
                        void getGoogleMeetConnectUrl().then((url) => window.location.assign(url))
                      }}
                    >
                      {t.connectGoogleMeetMedia}
                    </button>
                  ) : null}
                </div>
            ) : null}
            </div>
          )}
          </CreateMeetingSection>
          )}

          {/* Always a step, even for an internal meeting — that one still has
              a language, it just has no direction to translate in. Gating the
              whole step on the direction took the language select away with
              it. */}
          <CreateMeetingSection
            step={3}
            title={t.createMeetingStepLanguage}
            testId="create-meeting-step-language"
          >
          {!isInternalSession && (
          <div role="group" aria-label={t.translationLabel} className="space-y-2.5">
            {/* A segmented pair, where it used to be two description cards
                stacked beside each other. The descriptions did not disappear:
                whichever mode is on says what it does under the languages,
                which is where the answer is actually needed — the choice above
                is between two names, and two paragraphs to pick between them
                made step 3 taller than the two steps before it put together. */}
            <div
              data-testid="translation-mode-segmented"
              className="flex rounded-xl bg-slate-100 p-1 dark:bg-white/5"
            >
              {(
                [
                  ['one_way', t.translateOneWay, EMPTY_SOURCE_LANG],
                  ['two_way', t.translateTwoWay, 'ja'],
                ] as const
              ).map(([value, label, nextSourceLang]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    setMode(value)
                    setSourceLang(nextSourceLang)
                  }}
                  disabled={isStarting || isJoiningSharedRoom}
                  aria-pressed={mode === value}
                  data-testid={`translation-mode-${value}`}
                  className={[
                    'h-10 flex-1 rounded-[9px] text-sm transition disabled:cursor-not-allowed disabled:opacity-50',
                    mode === value
                      ? 'bg-white font-semibold text-[var(--accent)] shadow-sm dark:bg-[var(--bg-tertiary)]'
                      : 'font-medium text-[var(--text-secondary)]',
                  ].join(' ')}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* The languages belong to the step that chose the direction, not to
              a row of their own further down: picking Two-way is what makes
              the source language answerable at all. */}
          {isInternalSession ? (
            <div>
              <SelectField
                id={targetLangId}
                label={t.meetingLanguage}
                value={targetLang}
                onChange={setTargetLang}
                disabled={isStarting}
              >
                {LANGUAGES.map((language) => (
                  <option key={language.code} value={language.code}>
                    {getLanguageDisplayName(language.code, t)}
                  </option>
                ))}
              </SelectField>
            </div>
          ) : (
            <div
              className={[
                'grid items-end',
                isPanel ? 'gap-2' : 'gap-2 sm:gap-3 md:grid-cols-[1fr_auto_1fr]',
              ].join(' ')}
            >
              <div>
                <SelectField
                  id={sourceLangId}
                  label={t.transcriptionLanguage}
                  value={sourceLang}
                  onChange={setSourceLang}
                  disabled={mode !== 'two_way' || isStarting || isJoiningSharedRoom}
                >
                  {mode === 'one_way' && <option value="">{t.autoDetect}</option>}
                  {LANGUAGES.map((language) => (
                    <option key={language.code} value={language.code}>
                      {getLanguageDisplayName(language.code, t)}
                    </option>
                  ))}
                </SelectField>
              </div>

              {/* Which way the translation runs, said once in the layout
                  rather than only in the two labels. Hidden below `md`, where
                  the selects stack and an arrow would point at the wrong
                  thing; decorative either way, since the labels carry it. */}
              <span
                aria-hidden="true"
                className="hidden h-10 items-center text-[var(--text-tertiary)] md:flex"
              >
                →
              </span>

              <div>
                <SelectField
                  id={targetLangId}
                  label={t.translationLanguage}
                  value={targetLang}
                  onChange={setTargetLang}
                  disabled={isStarting || isJoiningSharedRoom}
                >
                  {LANGUAGES.map((language) => (
                    <option key={language.code} value={language.code}>
                      {getLanguageDisplayName(language.code, t)}
                    </option>
                  ))}
                </SelectField>
              </div>
            </div>
          )}

          {/* The description of whichever mode is on, under the languages it
              describes. It used to be the body of the card that chose it, and
              a reader only ever needed one of the two. */}
          {!isInternalSession && (
            <p
              data-testid="translation-mode-hint"
              className="text-xs leading-5 text-[var(--text-tertiary)]"
            >
              {mode === 'one_way' ? t.translateOneWayDesc : t.translateTwoWayDesc}
            </p>
          )}
          </CreateMeetingSection>
          </div>

          <div className="space-y-3 sm:space-y-4">
          {/* The right-hand column says what it is for once, at the top,
              instead of leaving three unrelated fields to explain themselves.
              Out of the panel, which is one column and would read it as a
              fourth step rather than as the other half of the form. */}
          {!isPanel && (
            <div data-testid="create-meeting-help-heading">
              <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">
                {t.createMeetingHelpTitle}{' '}
                <span className="font-normal text-[var(--text-tertiary)]">
                  {t.createMeetingHelpOptional}
                </span>
              </h3>
              <p className="mt-1 text-xs leading-5 text-[var(--text-secondary)]">
                {t.createMeetingHelpSubtitle}
              </p>
            </div>
          )}
          <div>
            <label
              className={[
                'flex items-center gap-1.5 text-xs font-semibold text-[var(--text-secondary)]',
                isPanel ? 'mb-1.5' : 'mb-1 sm:mb-1.5',
              ].join(' ')}
            >
              <ContextIcon className="h-3 w-3" />
              {t.contextOptional}
            </label>
            {/* One box, not two stacked in one shell. The file area used to be
                a strip glued under the text with a shared border, which read
                as a toolbar on the textarea rather than as somewhere to drop
                a file — and it held the text box to 80px to make room. */}
            <div
              className={[
                'overflow-hidden rounded-[10px] border border-slate-300 bg-white transition focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 dark:border-[var(--border-color)] dark:bg-[var(--bg-primary)] dark:focus-within:border-[var(--accent)] dark:focus-within:ring-[var(--ring)]',
                isPanel ? 'mt-4' : 'mt-3 sm:mt-4',
              ].join(' ')}
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleContextFileDrop}
            >
              <div className="relative">
                <textarea
                  id={contextId}
                  value={contextText}
                  onChange={(event) => {
                    setContextText(
                      clampToMaxLength(event.target.value, MEETING_CONTEXT_TEXT_MAX_LENGTH)
                    )
                    setContextTextError(null)
                  }}
                  placeholder={t.contextPlaceholder}
                  rows={3}
                  maxLength={MEETING_CONTEXT_TEXT_MAX_LENGTH}
                  disabled={isStarting}
                  className="min-h-[152px] w-full resize-none border-0 bg-white px-3 py-2 text-base text-slate-700 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-[var(--bg-primary)] dark:text-[var(--text-secondary)] dark:placeholder:text-[var(--text-tertiary)] sm:px-4 sm:py-3 sm:text-sm"
                />
                <span className="absolute bottom-3 right-3 text-[10px] font-semibold text-slate-600 dark:text-[var(--text-tertiary)]">
                  {contextText.length} / {MEETING_CONTEXT_TEXT_MAX_LENGTH}
                </span>
              </div>
            </div>

            {/* Its own dashed box under the text, not a strip glued to the
                bottom of it. As a strip it read as a toolbar on the textarea
                rather than as somewhere to drop a file, and it held the text
                box down to 80px to make room for itself. */}
            <label
              data-testid="context-dropzone"
              className={[
                'mt-3 flex min-h-[107px] flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-[#c7d2fe] bg-white px-3.5 py-4 text-center transition dark:border-[var(--border-color)] dark:bg-[var(--bg-primary)]',
                isStarting
                  ? 'cursor-not-allowed opacity-60'
                  : 'cursor-pointer hover:bg-blue-50/60 dark:hover:bg-[var(--accent-light)]',
              ].join(' ')}
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleContextFileDrop}
            >
                <input
                  type="file"
                  className="sr-only"
                  multiple
                  accept={CONTEXT_FILE_ACCEPT}
                  disabled={isStarting}
                  onChange={handleContextFileInputChange}
                />
              <PaperclipIcon className="h-5 w-5 text-slate-400 dark:text-[var(--text-tertiary)]" />
              <span className="text-xs font-bold text-slate-900 dark:text-[var(--text-primary)]">
                {isAnalyzingContext ? t.contextAnalyzing : t.contextFileDragDrop}{' '}
                <span className="font-bold text-[var(--accent)] underline">
                  {t.contextFileSelect}
                </span>
              </span>
              <span className="text-[10px] font-bold uppercase text-slate-500 dark:text-[var(--text-tertiary)]">
                {t.contextFileFormats}
              </span>
            </label>
            {contextTextError && (
              <p className="mt-2 text-xs text-[var(--error)]" role="alert">
                {contextTextError}
              </p>
            )}
            {contextFileError && (
              <p className="mt-2 text-xs text-[var(--error)]">{contextFileError}</p>
            )}

            {contextFiles.length > 0 && (
              <div className="mt-3 space-y-2 sm:mt-4">
                {contextFiles.map((item) => (
                  <FileStatusRow
                    key={item.id}
                    extension={getContextFileExtensionLabel(item.file.name)}
                    name={item.file.name}
                    size={formatFileSize(item.file.size)}
                    status={t.contextFileStatusDone}
                    tone="success"
                    onRemove={() => removeContextFile(item.id)}
                    removeLabel={t.removeContextFile}
                    disabled={isStarting}
                  />
                ))}
              </div>
            )}
          </div>

          {!isPanel && (
            <div>
              <div className="mb-1 flex items-center justify-between gap-4 sm:mb-1.5">
                <label
                  htmlFor={glossaryId}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[var(--text-secondary)]"
                >
                  <BookIcon className="h-3.5 w-3.5" />
                  {t.glossary}
                </label>
              </div>
              <GlossaryMultiSelect
                id={glossaryId}
                selectedIds={selectedGlossaryIds}
                onChange={setSelectedGlossaryIds}
                disabled={isStarting}
              />
              {glossaryConflictCount > 0 && !glossaryResolveError ? (
                <p className="mt-2 text-xs text-amber-600 dark:text-amber-400" role="status">
                  {fmt(t.glossaryConflictWarning, { count: glossaryConflictCount })}
                </p>
              ) : null}
              {glossaryResolveError ? (
                <p className="mt-2 text-xs text-[var(--error)]" role="alert">
                  {t.glossaryApplyFailed}
                </p>
              ) : null}
            </div>
          )}
          </div>

        </div>
        </div>

        <div
          className={[
            'shrink-0 border-[var(--border-color)]',
            isPanel
              ? 'px-3 py-3'
              : 'px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4',
          ].join(' ')}
        >
        <div className={['grid', isPanel ? 'grid-cols-2 gap-2' : 'grid-cols-2 gap-2 sm:gap-3'].join(' ')}>
          <button
            type="button"
            onClick={onClose}
            disabled={isStarting}
            className={[
              'rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-50',
              isPanel
                ? 'h-10 text-sm'
                : 'min-h-[44px] h-auto text-xs sm:text-sm',
            ].join(' ')}
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={handleStart}
            disabled={
              !title.trim() ||
              isAnalyzingContext ||
              isResolvingGlossary ||
              isResolvingTitle ||
              isStarting
            }
            className={[
              'btn-primary flex items-center justify-center gap-2 rounded-xl disabled:opacity-50',
              isPanel
                ? 'h-10 text-sm'
                : 'min-h-[44px] h-auto text-xs sm:text-sm',
            ].join(' ')}
          >
            <PlayCircleIcon className="h-4 w-4" />
            {isAnalyzingContext || isResolvingGlossary
              ? t.contextAnalyzing
              : isStarting
                ? t.starting
                : t.start}
          </button>
        </div>
        </div>
      </div>
    </div>
  )
}

interface FileStatusRowProps {
  extension: string
  name: string
  size: string
  status: string
  tone: 'success' | 'warning'
  onRemove: () => void
  removeLabel: string
  disabled?: boolean
}

function FileStatusRow({
  extension,
  name,
  size,
  status,
  tone,
  onRemove,
  removeLabel,
  disabled = false,
}: FileStatusRowProps) {
  const isSuccess = tone === 'success'

  return (
    <div
      className={[
        'flex min-h-[48px] items-center justify-between gap-3 rounded-md border px-3 sm:min-h-[54px]',
        isSuccess
          ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10'
          : 'border-slate-300 bg-slate-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)]',
      ].join(' ')}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={[
            'flex h-8 w-8 shrink-0 items-center justify-center rounded border bg-white text-[9px] font-bold dark:bg-[var(--bg-secondary)]',
            isSuccess
              ? 'border-red-200 text-red-500'
              : 'border-blue-200 text-blue-600 dark:border-[var(--border-color)] dark:text-[var(--accent)]',
          ].join(' ')}
        >
          {extension}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-xs font-bold text-slate-900 dark:text-[var(--text-primary)] sm:text-sm">
            {name}
          </span>
          <span className="block text-[10px] font-medium text-slate-500 dark:text-[var(--text-tertiary)]">
            {size}
          </span>
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-3 sm:gap-8">
        <span
          className={[
            'inline-flex items-center gap-1 text-[10px] font-bold uppercase',
            isSuccess ? 'text-emerald-600' : 'text-amber-600',
          ].join(' ')}
        >
          <span
            className={['h-2 w-2 rounded-full', isSuccess ? 'bg-emerald-500' : 'bg-amber-500'].join(
              ' '
            )}
          />
          {status}
        </span>
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="text-slate-500 transition hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-slate-500 dark:text-[var(--text-tertiary)]"
          aria-label={removeLabel}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

function PlayCircleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1.7 6.9 5 3.1-5 3.1V8.9Z" />
    </svg>
  )
}

function PaperclipIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m8.5 12.5 5.9-5.9a3.1 3.1 0 0 1 4.4 4.4l-7.2 7.2a5 5 0 0 1-7.1-7.1l7.6-7.6"
      />
    </svg>
  )
}

function ContextIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path d="M3 2.75h10M3 6h10M3 9.25h6M3 12.5h4" strokeLinecap="round" />
    </svg>
  )
}
